import { ColDef } from 'ag-grid-community';
import { t } from 'i18next';

import { LinkCell, StatusCell } from 'core/elements';
import { LinkTarget } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { formatDate, getPercentage } from 'core/utils';
import { CustomerContactCell, PricingProfileCell } from 'parts/elements';
import { IPartHistoryItem, IReusePriceConfig } from 'parts/types';
import { PricingPerUomCell } from 'pricing/elements';
import { PricingHelper } from 'pricing/helpers';
import { getQuoteStatuses } from 'quotes/components/CustomerQuoteList/Table/Columns/utils';
import { statusColorMap } from 'quotes/constants';
import { QuoteEntityType, QuoteStatus, QuoteStatusLabel } from 'quotes/enums';
import { quotePartsPath } from 'quotes/routes/routePaths';

import { PriceCell } from './cells';

interface IColumnDefOptions {
  reusePrice?: IReusePriceConfig;
}

export const columnDef = ({ reusePrice }: IColumnDefOptions = {}): ColDef[] => [
  {
    colId: 'price',
    headerName: t('headerPrice'),
    type: 'rightAligned',
    field: 'price',
    filter: 'agNumberColumnFilter',
    cellRenderer: PriceCell,
    cellRendererParams: {
      reusePrice,
    },
  },
  {
    colId: 'quantity',
    headerName: t('headerQuantity'),
    type: 'rightAligned',
    field: 'quantity',
    filter: 'agNumberColumnFilter',
    valueFormatter: ({ data }) => `${NumberHelper.format({ amount: data?.quantity })} ${data?.uom}`,
  },
  {
    colId: 'customerContactGroupName',
    headerName: t('headerCustomer'),
    field: 'customerContactGroupName',
    filter: 'agTextColumnFilter',
    sortable: false,
    cellRenderer: CustomerContactCell,
  },
  {
    colId: 'jobName',
    headerName: t('headerJobName'),
    field: 'jobName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'quoteUserLocationName',
    headerName: t('headerLocation'),
    field: 'quoteUserLocationName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'quoteUserCorporateRegionName',
    headerName: t('headerRegion'),
    field: 'quoteUserCorporateRegionName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'quoteId',
    headerName: t('headerQuoteId'),
    field: 'quoteId',
    filter: 'agNumberColumnFilter',
    cellRenderer: LinkCell,
    cellRendererParams: {
      columnId: 'quoteId',
      getPath: (data: IPartHistoryItem) => quotePartsPath(data.quoteId),
      target: LinkTarget.BLANK,
    },
  },
  {
    colId: 'updatedAt',
    headerName: t('headerDate'),
    field: 'updatedAt',
    filter: 'agDateColumnFilter',
    valueFormatter: ({ value }) => value && formatDate(value),
  },
  {
    colId: 'quotedByUserName',
    headerName: t('headerQuotedBy'),
    field: 'quotedByUserName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'cost',
    headerName: t('headerCost'),
    maxWidth: 150,
    type: 'rightAligned',
    field: 'cost',
    filter: 'agNumberColumnFilter',
    cellRenderer: PricingPerUomCell,
    cellRendererParams: {
      columnId: 'cost',
      pricingField: 'cost',
    },
  },
  {
    colId: 'margin',
    headerName: t('headerMargin'),
    maxWidth: 130,
    type: 'rightAligned',
    sortable: false,
    valueGetter: ({ data }) => PricingHelper.computeGpm(data?.cost, data?.price),
    valueFormatter: ({ data }) =>
      data && getPercentage(PricingHelper.computeGpm(data?.cost, data?.price)),
  },
  {
    colId: 'profile',
    headerName: t('headerProfile'),
    field: 'priceSourcePricingProfileName',
    sortable: false,
    cellRenderer: PricingProfileCell,
  },
  {
    colId: 'status',
    headerName: t('headerStatus'),
    maxWidth: 150,
    field: 'status',
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: ({ value }: { value: QuoteStatus }) => QuoteStatusLabel[value],
      values: getQuoteStatuses(QuoteEntityType.QUOTE),
      defaultToNothingSelected: true,
    },
    cellRenderer: StatusCell,
    cellRendererParams: { colorMap: statusColorMap, labelMap: QuoteStatusLabel },
  },
];
